import { State } from "common/types/redux";

import {
  getCourseName,
  getSectionTitle
} from "seneca-common/features/course/state";
import {
  getEnrolledClasses,
  getFetchClassEnrollmentsSuccess
} from "seneca-common/features/enrollments/features/class-enrollments/state";
import {
  allSectionStatsForCourseFetchSucceeded,
  courseStatFetchSucceeded,
  getCourseSessionsCompleted,
  getCourseTotalStudyTime,
  getSectionTimesStudied,
  getSectionTotalStudyTime
} from "seneca-common/features/stats/state";

import isSmartLearningActive from "features/classroom/selectors/isSmartLearningActive";

export type SessionAnalyticsPayload = {
  courseId: string;
  sectionId: string;
  smartLearningActive: boolean;
  courseName: string;
  sectionTitle: string;
  totalCourseLearningTime: number | null;
  totalCourseSessionsCompleted: number | null;
  totalSectionLearningTime: number | null;
  totalSectionSessionsCompleted: number | null;
  partOfClass?: boolean;
};

export default function generateSessionAnalyticsPayload(
  state: State,
  courseId: string,
  sectionId: string
): SessionAnalyticsPayload {
  const smartLearningActive = isSmartLearningActive(state);

  return {
    courseId,
    sectionId,
    smartLearningActive,
    partOfClass: getIsPartOfClass(state, courseId),
    ...getCourseAndSectionNames(state, courseId, sectionId),
    ...getCourseStats(state, courseId),
    ...getSectionStats(state, courseId, sectionId)
  };
}

function getCourseAndSectionNames(
  state: any,
  courseId: string,
  sectionId: string
) {
  return {
    courseName: getCourseName(state, courseId),
    sectionTitle: getSectionTitle(state, sectionId)
  };
}

function getCourseStats(state: State, courseId: string) {
  const courseStatFetched = courseStatFetchSucceeded(state, courseId);

  return {
    totalCourseLearningTime: courseStatFetched
      ? getCourseTotalStudyTime(state, courseId)
      : null,
    totalCourseSessionsCompleted: courseStatFetched
      ? getCourseSessionsCompleted(state, courseId)
      : null
  };
}

function getSectionStats(state: State, courseId: string, sectionId: string) {
  const sectionStatsFetched = allSectionStatsForCourseFetchSucceeded(
    state,
    courseId
  );

  return {
    totalSectionLearningTime: sectionStatsFetched
      ? getSectionTotalStudyTime(state, sectionId)
      : null,
    totalSectionSessionsCompleted: sectionStatsFetched
      ? getSectionTimesStudied(state, sectionId)
      : null
  };
}

function getIsPartOfClass(state: State, courseId: string) {
  const classEnrollmentsFetched = getFetchClassEnrollmentsSuccess(state);

  if (!classEnrollmentsFetched) {
    return undefined;
  }

  return getEnrolledClasses(state).some(emt =>
    emt.courseIds.includes(courseId)
  );
}
